import React, { useState, useEffect } from 'react';
import { 
  Target, 
  CheckCircle2, 
  Lightbulb, 
  Loader2, 
  ChevronRight, 
  ChevronLeft,
  Plus, 
  FileText, 
  Edit3,
  Clock,
  AlertCircle,
  TrendingUp,
  Award,
  Sparkles,
  ChevronDown,
  ChevronUp,
  User,
  Bookmark
} from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { enrichAllTopics, enrichTopicWithMetrics } from './utils/topicMetrics';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

// Sub-component: Rating Dots Display
const RatingDots = ({ value, maxValue = 5, activeColor = 'bg-stone-600', inactiveColor = 'bg-stone-200' }) => {
  return (
    <div className="flex gap-1">
      {[...Array(maxValue)].map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${i < value ? activeColor : inactiveColor}`}
        />
      ))}
    </div>
  );
};

// Sub-component: Quick Comparison Table (matching mockup)
const QuickComparisonTable = ({ topics, onTopicClick, expandedTopic }) => {
  if (!topics || topics.length === 0) return null;
  
  return (
    <div className="mb-8 bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
      <div className="px-6 py-4 bg-slate-900/5 border-b border-slate-900/10">
        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
          Quick Comparison
        </h2>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                Topic
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                Uniqueness
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                Evidence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wide">
                Difficulty
              </th>
            </tr>
          </thead>
          <tbody>
            {topics.map((topic, idx) => {
              const isPowerStory = topic.id === 'gold_story' || topic.recommended || topic.isPowerStory;
              
              return (
                <tr 
                  key={topic.id}
                  className={`border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors ${
                    isPowerStory ? 'bg-gold-50/50' : ''
                  } ${expandedTopic === topic.id ? 'bg-slate-50' : ''}`}
                  onClick={() => onTopicClick(topic.id)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div className="text-sm font-medium text-stone-400 pt-0.5">
                        {idx + 1}.
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="text-sm font-medium text-slate-900">
                            {topic.title.replace('🏆 ', '')}
                          </div>
                          {isPowerStory && (
                            <span className="px-2 py-0.5 bg-violet-600 text-white text-xs font-bold rounded-full uppercase">
                              BEST
                            </span>
                          )}
                        </div>
                        {topic.badges && topic.badges.length > 0 && (
                          <div className="flex gap-1.5 mt-1 flex-wrap">
                            {topic.badges.slice(0, 2).map((badge, i) => (
                              <div
                                key={i}
                                className="text-xs px-2 py-0.5 rounded font-medium bg-white border border-slate-900"
                              >
                                {badge.label || badge}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <RatingDots value={topic.metrics?.uniqueness || 3} activeColor="bg-gold-400" />
                  </td>
                  <td className="px-6 py-4">
                    <RatingDots value={topic.metrics?.evidenceScore || 3} activeColor="bg-teal-500" />
                  </td>
                  <td className="px-6 py-4">
                    <RatingDots value={topic.metrics?.writingDifficulty || 3} activeColor="bg-coral-500" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Sub-component: Expanded Topic Details (matching mockup)
const ExpandedTopicDetails = ({ topic, index, onSave, isSaving }) => {
  if (!topic) return null;
  
  const isPowerStory = topic.id === 'gold_story' || topic.recommended || topic.isPowerStory;
  
  // Color classes for metrics dots
  const colorClasses = {
    gold: 'bg-[#ffd54f]',
    teal: 'bg-[#26a69a]',
    indigo: 'bg-[#7e57e2]',
    coral: 'bg-[#ff6b6b]',
    azure: 'bg-[#4290f5]'
  };
  
  const renderDots = (count, max = 5, color = 'slate') => {
    const colorClass = colorClasses[color] || 'bg-slate-900';
    return (
      <div className="flex gap-1">
        {[...Array(max)].map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full ${
              i < count ? colorClass : 'bg-stone-200'
            }`}
          />
        ))}
      </div>
    );
  };
  
  // Determine accent color based on topic
  const accentColor = isPowerStory ? 'gold' : 
                      topic.id?.startsWith('strategic-') ? 'indigo' :
                      topic.id?.startsWith('topic_') ? 'teal' : 'coral';
  
  // Get time estimate
  const timeEstimate = topic.metrics?.estimatedTime || topic.estimatedTime || "4-5 hours";
  
  // Get uniqueness, evidence, difficulty values
  const uniqueness = topic.metrics?.uniqueness || 3;
  const evidence = topic.metrics?.evidenceScore || 3;
  const difficulty = topic.metrics?.writingDifficulty || 3;
  
  return (
    <div className="bg-white border-2 border-slate-900 rounded-xl p-6 mb-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-stone-200">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-medium text-stone-400">
              {index + 1}.
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              {topic.title.replace('🏆 ', '')}
            </h3>
          </div>
          
          {topic.badges && topic.badges.length > 0 && (
            <div className="flex gap-2 mb-3 flex-wrap">
              {isPowerStory && (
                <div className="text-xs px-3 py-1 rounded-lg font-bold bg-gold-400 text-slate-900 uppercase border border-slate-900">
                  Top Pick
                </div>
              )}
              {topic.badges.slice(0, 2).map((badge, i) => (
                <div
                  key={i}
                  className="text-xs px-3 py-1 bg-white border border-slate-900 rounded-lg font-medium"
                >
                  {badge.label || badge}
                </div>
              ))}
            </div>
          )}
          
          <p className="text-sm text-stone-700 leading-relaxed">
            {topic.description}
          </p>
        </div>
        
        <button className="p-2 hover:bg-stone-50 rounded-lg transition-colors ml-4">
          <Bookmark className="w-5 h-5 text-stone-400" />
        </button>
      </div>

      {/* Why This Wins */}
      {topic.whyThisWins && (
        <div className="mb-6">
          <div className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">
            Why This Wins
          </div>
          <p className="text-sm text-stone-700 leading-relaxed">
            {topic.whyThisWins}
          </p>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-6 mb-6 pb-6 border-b border-stone-200">
        <div>
          <div className="text-xs text-stone-500 mb-2">Uniqueness</div>
          {renderDots(uniqueness, 5, accentColor)}
          <div className="text-xs text-stone-500 mt-1">
            {uniqueness === 5 ? 'Very High' : uniqueness === 4 ? 'High' : uniqueness === 3 ? 'Medium' : 'Low'}
          </div>
        </div>
        <div>
          <div className="text-xs text-stone-500 mb-2">Evidence</div>
          {renderDots(evidence, 5, accentColor)}
          <div className="text-xs text-stone-500 mt-1">
            {evidence === 5 ? 'Concrete' : evidence === 4 ? 'Strong' : evidence === 3 ? 'Moderate' : evidence === 2 ? 'Limited' : 'Weak'}
          </div>
        </div>
        <div>
          <div className="text-xs text-stone-500 mb-2">Difficulty</div>
          {renderDots(difficulty, 5, accentColor)}
          <div className="text-xs text-stone-500 mt-1">
            {difficulty === 2 ? 'Easy' : difficulty === 3 ? 'Medium' : difficulty === 4 ? 'Challenging' : 'Very Hard'}
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-stone-600">
          <Clock className="w-4 h-4" />
          {timeEstimate}
        </div>
        
        <button 
          onClick={() => onSave && onSave(topic)}
          disabled={isSaving}
          className={`px-6 py-2.5 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
            isSaving 
              ? 'bg-slate-600 cursor-not-allowed' 
              : 'bg-slate-900 hover:bg-slate-800'
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Bookmark className="w-4 h-4" />
              Save to Ideas
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Sub-component: Enhanced Topic Card
const TopicCard = ({ topic, index, onSelect, isExpanded, onToggleExpand }) => {
  const isFeatured = topic.recommended || topic.isPowerStory;
  
  return (
    <div 
      className={`
        bg-white rounded-xl border-2 transition-all cursor-pointer
        ${topic.id?.startsWith('strategic-') 
          ? 'border-violet-300 bg-gradient-to-br from-white to-violet-50/30 shadow-md hover:shadow-lg hover:border-violet-400' 
          : topic.id === 'gold_story'
          ? 'border-amber-300 bg-gradient-to-br from-white to-amber-50/30 shadow-md hover:shadow-lg hover:border-amber-400'
          : isFeatured 
          ? 'border-violet-300 bg-gradient-to-br from-white to-violet-50 shadow-md hover:shadow-lg hover:border-violet-400' 
          : 'border-stone-200 hover:border-stone-300 hover:shadow-md'
        }
      `}
    >
      {/* Card Header */}
      <div className="p-6">
        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          {topic.badges?.map((badge, i) => (
            <span
              key={i}
              className={`
                px-2.5 py-1 text-xs font-semibold rounded-md uppercase tracking-wide
                ${badge.type === 'primary' ? 'bg-violet-600 text-white' : ''}
                ${badge.type === 'strength' ? 'bg-emerald-100 text-emerald-700' : ''}
                ${badge.type === 'philosophical' ? 'bg-azure-100 text-azure-700' : ''}
                ${badge.type === 'exclusive' ? 'bg-amber-100 text-amber-700' : ''}
              `}
            >
              {badge.label}
            </span>
          ))}
          
          {/* Content Type Badge - distinguish strategic insights from assessment */}
          {topic.id?.startsWith('strategic-') && (
            <span className="px-2.5 py-1 text-xs font-semibold rounded-md uppercase tracking-wide bg-gradient-to-r from-violet-500 to-pink-500 text-white">
              🔮 Hidden Story
            </span>
          )}
          {topic.id === 'gold_story' && (
            <span className="px-2.5 py-1 text-xs font-semibold rounded-md uppercase tracking-wide bg-gradient-to-r from-amber-500 to-orange-500 text-white">
              🏆 Top Pick
            </span>
          )}
          {topic.id?.startsWith('topic_') && (
            <span className="px-2.5 py-1 text-xs font-semibold rounded-md uppercase tracking-wide bg-gradient-to-r from-slate-400 to-slate-500 text-white">
              📊 From Assessment
            </span>
          )}
        </div>
        
        {/* Title and Description */}
        <div className="mb-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <p className="text-sm font-semibold text-stone-500 mb-1">
                {index + 1}.
              </p>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {topic.title}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {topic.description}
              </p>
            </div>
            <div className={`
              ml-4 w-12 h-12 rounded-xl flex items-center justify-center
              ${topic.id?.startsWith('strategic-') 
                ? 'bg-gradient-to-br from-violet-500 to-pink-500' 
                : topic.id === 'gold_story'
                ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                : isFeatured 
                ? 'bg-gradient-to-br from-violet-500 to-violet-600' 
                : 'bg-gradient-to-br from-slate-400 to-slate-500'
              }
            `}>
              {topic.id?.startsWith('strategic-') ? (
                <Sparkles className="w-6 h-6 text-white" />
              ) : topic.id === 'gold_story' ? (
                <Award className="w-6 h-6 text-white" />
              ) : (
                <FileText className="w-6 h-6 text-white" />
              )}
            </div>
          </div>
        </div>
        
        {/* Strength Callout */}
        {topic.competitiveAdvantage && (
          <div className={`
            p-3 rounded-lg border-l-3 mb-4
            ${isFeatured 
              ? 'bg-violet-50 border-violet-400' 
              : 'bg-stone-50 border-stone-400'
            }
          `}>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
              isFeatured ? 'text-violet-700' : 'text-stone-600'
            }`}>
              Why This Wins
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              {topic.competitiveAdvantage}
            </p>
          </div>
        )}
        
        {/* Quick Metrics Grid */}
        <div className="grid grid-cols-3 gap-3 p-3 bg-stone-50 rounded-lg mb-4">
          <div className="text-center">
            <p className="text-xs font-semibold text-stone-600 mb-1">Uniqueness</p>
            <div className="flex justify-center mb-1">
              <RatingDots value={topic.metrics?.uniqueness || 3} activeColor="bg-violet-500" />
            </div>
            <p className="text-xs text-stone-500">
              {topic.metrics?.uniqueness >= 4 ? 'Very High' : topic.metrics?.uniqueness >= 2 ? 'Moderate' : 'Common'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs font-semibold text-stone-600 mb-1">Evidence</p>
            <div className="flex justify-center mb-1">
              <RatingDots value={topic.metrics?.evidenceScore || 3} activeColor="bg-emerald-500" />
            </div>
            <p className="text-xs text-stone-500 capitalize">{topic.metrics?.evidenceStrength || 'Moderate'}</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-semibold text-stone-600 mb-1">Difficulty</p>
            <div className="flex justify-center mb-1">
              <RatingDots value={topic.metrics?.writingDifficulty || 3} activeColor="bg-amber-500" />
            </div>
            <p className="text-xs text-stone-500">
              {topic.metrics?.writingDifficulty <= 2 ? 'Easy' : topic.metrics?.writingDifficulty >= 4 ? 'Challenging' : 'Moderate'}
            </p>
          </div>
        </div>
        
        {/* Story Context (if available) */}
        {topic.storyContext && (
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-azure-700 mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Story Behind This Topic
            </p>
            <p className="text-sm text-azure-900 italic">"{topic.storyContext}"</p>
            {topic.relatedQuestion && (
              <p className="text-xs text-azure-600 mt-2">From: {topic.relatedQuestion}</p>
            )}
          </div>
        )}
        
        {/* Expandable Details */}
        {isExpanded && topic.riskFactors && topic.riskFactors.length > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Considerations
            </p>
            <ul className="text-sm text-amber-900 space-y-1">
              {topic.riskFactors.map((risk, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Card Actions */}
      <div className="border-t border-stone-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-stone-600">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{topic.metrics?.timeInvestment || '3-4 hours'}</span>
          </div>
          {isFeatured && (
            <div className="flex items-center gap-1 text-violet-600 font-medium">
              <TrendingUp className="w-4 h-4" />
              <span>Easiest to write</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="text-stone-500 hover:text-stone-700 p-2"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onSelect(topic)}
            className={`
              px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2
              ${isFeatured 
                ? 'bg-violet-600 text-white hover:bg-violet-700 px-5 py-2.5' 
                : 'bg-stone-700 text-white hover:bg-stone-800'
              }
            `}
          >
            Develop this topic
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function StrategicEssayTopicsV2({ onComplete, assessmentData, insights, addEssay, mode }) {
  const { currentUser } = useFamilyContext();
  const [phase, setPhase] = useState('selection'); // 'selection', 'development', 'processing', 'results'
  const [strategicInsightsData, setStrategicInsightsData] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedTopicType, setSelectedTopicType] = useState('all');
  const [developmentResponses, setDevelopmentResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [finalOutline, setFinalOutline] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dynamicQuestions, setDynamicQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionGenerationError, setQuestionGenerationError] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [enrichedTopics, setEnrichedTopics] = useState([]);
  const [savingTopicId, setSavingTopicId] = useState(null); // Track which topic is being saved

  // Load strategic insights data from database or sessionStorage
  const loadStrategicInsights = async (userId) => {
    try {
      console.log('🔍 Loading strategic insights from database for user:', userId);
      
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('depth_analysis, depth_analysis_created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading strategic insights:', error);
        return null;
      }

      if (!data || !data.depth_analysis) {
        console.log('📭 No strategic insights found in database');
        return null;
      }

      // Transform the database analysis into the format expected by strategic insights
      const analysisData = data.depth_analysis;
      const strategicData = {
        deeperInsights: analysisData.deeperInsights || {},
        competitiveAdvantages: analysisData.competitiveAdvantages || {},
        analysisText: analysisData.improvementOpportunities || '',
        improvementOpportunities: analysisData.improvementOpportunities || '',
        sourceUserId: userId
      };

      console.log('✅ Loaded strategic insights from database:', strategicData);
      return strategicData;
    } catch (error) {
      console.error('Error loading strategic insights:', error);
      return null;
    }
  };

  // Check for strategic insights data from Profile Hub or load from database if mode is insights
  useEffect(() => {
    const loadInsights = async () => {
      // First check sessionStorage (for direct navigation from Profile Hub)
      const strategicData = sessionStorage.getItem('strategicInsightsContext');
      if (strategicData) {
        try {
          const parsedData = JSON.parse(strategicData);
          setStrategicInsightsData(parsedData);
          console.log('✨ Strategic insights loaded from sessionStorage:', parsedData);
          sessionStorage.removeItem('strategicInsightsContext'); // Clean up after use
          return;
        } catch (error) {
          console.error('Error parsing strategic insights data:', error);
        }
      }

      // If mode is 'insights', load from database
      if (mode === 'insights' && assessmentData?.userId) {
        const dbInsights = await loadStrategicInsights(assessmentData.userId);
        if (dbInsights) {
          setStrategicInsightsData(dbInsights);
        }
      }
    };

    loadInsights();
  }, [mode, assessmentData]);

  // Deduplication utilities
  const normalizeText = (text) => {
    return text?.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim() || '';
  };

  const calculateSimilarity = (text1, text2) => {
    const normalized1 = normalizeText(text1);
    const normalized2 = normalizeText(text2);
    
    if (!normalized1 || !normalized2) return 0;
    
    // Check for exact match
    if (normalized1 === normalized2) return 1;
    
    // Check for substring containment (bidirectional)
    if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
      return 0.8;
    }
    
    // Check for significant word overlap
    const words1 = normalized1.split(' ').filter(w => w.length > 3);
    const words2 = normalized2.split(' ').filter(w => w.length > 3);
    
    if (words1.length === 0 || words2.length === 0) return 0;
    
    const commonWords = words1.filter(word => words2.includes(word));
    const similarity = (2 * commonWords.length) / (words1.length + words2.length);
    
    return similarity;
  };

  const isDuplicateTopic = (newTopic, existingTopics, threshold = 0.6) => {
    for (const existing of existingTopics) {
      // Compare titles
      const titleSimilarity = calculateSimilarity(newTopic.title, existing.title);
      if (titleSimilarity >= threshold) {
        console.log(`🔄 Duplicate detected (title): "${newTopic.title}" similar to "${existing.title}" (${titleSimilarity.toFixed(2)})`);
        return true;
      }
      
      // Compare descriptions
      const descSimilarity = calculateSimilarity(newTopic.description, existing.description);
      if (descSimilarity >= threshold) {
        console.log(`🔄 Duplicate detected (description): "${newTopic.title}" similar to "${existing.title}" (${descSimilarity.toFixed(2)})`);
        return true;
      }
      
      // Compare differentiators
      const diffSimilarity = calculateSimilarity(newTopic.differentiator, existing.differentiator);
      if (diffSimilarity >= 0.7) { // Higher threshold for differentiators
        console.log(`🔄 Duplicate detected (differentiator): "${newTopic.title}" similar to "${existing.title}" (${diffSimilarity.toFixed(2)})`);
        return true;
      }
      
      // Compare full text content if available
      const fullText1 = newTopic.fullText || `${newTopic.title} ${newTopic.description}`;
      const fullText2 = existing.fullText || `${existing.title} ${existing.description}`;
      const contentSimilarity = calculateSimilarity(fullText1, fullText2);
      if (contentSimilarity >= threshold) {
        console.log(`🔄 Duplicate detected (content): "${newTopic.title}" similar to "${existing.title}" (${contentSimilarity.toFixed(2)})`);
        return true;
      }
    }
    
    return false;
  };

  const removeDuplicateTopics = (topics) => {
    const uniqueTopics = [];
    const duplicateCount = { removed: 0, total: topics.length };
    
    for (const topic of topics) {
      if (!isDuplicateTopic(topic, uniqueTopics)) {
        uniqueTopics.push(topic);
      } else {
        duplicateCount.removed++;
      }
    }
    
    if (duplicateCount.removed > 0) {
      console.log(`🧹 Removed ${duplicateCount.removed} duplicate topics out of ${duplicateCount.total} total`);
    }
    
    return uniqueTopics;
  };

  // Parse structured analysis text to extract discovery probes
  const parseAnalysisForDiscoveryProbes = (analysisText) => {
    if (!analysisText || typeof analysisText !== 'string') return [];
    
    const probes = [];
    
    // Extract bullet points from different sections
    const extractBullets = (sectionText) => {
      const bullets = [];
      const bulletMatches = sectionText.match(/•\s+([^•\n]+)/g);
      if (bulletMatches) {
        bullets.push(...bulletMatches.map(match => match.replace(/^•\s+/, '').trim()));
      }
      return bullets;
    };
    
    // Section 1: Areas for deeper exploration
    const deeperExplorationMatch = analysisText.match(/Areas for deeper exploration:([\s\S]*?)(?=\n\s*🛡️|🛡️|📖|🎭|$)/i);
    if (deeperExplorationMatch) {
      const bullets = extractBullets(deeperExplorationMatch[1]);
      bullets.slice(0, 2).forEach((bullet, index) => {
        // Remove question marks and convert to discovery probe
        const cleanedBullet = bullet.replace(/\?$/, '').replace(/^What |^How |^Why /, '');
        probes.push({
          id: `exploration-${index}`,
          title: cleanedBullet,
          description: 'An area where you might have untold stories that could reveal depth and authenticity',
          probeType: 'exploration',
          badges: [{ label: 'Deep Dive', type: 'primary' }],
          competitiveAdvantage: 'Reveals personal motivations and experiences that set you apart',
          isPowerStory: true,
          recommended: index === 0,
          fullText: bullet,
          discoveryPrompt: `Tell me about ${cleanedBullet.toLowerCase()}`
        });
      });
    }

    // Section 2: Potential areas of hesitation (vulnerability probes)
    const hesitationMatch = analysisText.match(/Potential areas of hesitation:([\s\S]*?)(?=\n\s*📖|📖|🎭|$)/i);
    if (hesitationMatch) {
      const bullets = extractBullets(hesitationMatch[1]);
      bullets.slice(0, 2).forEach((bullet, index) => {
        probes.push({
          id: `vulnerability-${index}`,
          title: bullet,
          description: 'An opportunity to show vulnerability and genuine personal growth',
          probeType: 'vulnerability',
          badges: [{ label: 'Authentic', type: 'philosophical' }],
          competitiveAdvantage: 'Shows self-awareness and emotional intelligence through honest reflection',
          recommended: false,
          fullText: bullet,
          discoveryPrompt: `Share a specific experience related to ${bullet.toLowerCase()}`
        });
      });
    }

    // Section 3: Deeper stories to explore
    const deeperStoriesMatch = analysisText.match(/Deeper stories to explore:([\s\S]*?)(?=\n\s*🎭|🎭|$)/i);
    if (deeperStoriesMatch) {
      const bullets = extractBullets(deeperStoriesMatch[1]);
      bullets.slice(0, 2).forEach((bullet, index) => {
        probes.push({
          id: `deeper-story-${index}`,
          title: bullet,
          description: 'A specific narrative angle that could reveal compelling personal insights',
          probeType: 'narrative',
          badges: [{ label: 'Hidden Story', type: 'strength' }],
          competitiveAdvantage: 'Uncovers experiences that demonstrate growth and character in unique ways',
          recommended: false,
          fullText: bullet,
          discoveryPrompt: `Walk me through ${bullet.toLowerCase()}`
        });
      });
    }

    // Section 4: Authenticity opportunities
    const authenticityMatch = analysisText.match(/Authenticity opportunities:([\s\S]*?)$/i);
    if (authenticityMatch) {
      const bullets = extractBullets(authenticityMatch[1]);
      bullets.slice(0, 1).forEach((bullet, index) => {
        probes.push({
          id: `authenticity-${index}`,
          title: bullet,
          description: 'An opportunity to move beyond polished responses to show your real self',
          probeType: 'authenticity',
          badges: [{ label: 'Real Self', type: 'exclusive' }],
          competitiveAdvantage: 'Breaks through generic application responses to reveal genuine personality',
          recommended: false,
          fullText: bullet,
          discoveryPrompt: `Help me understand ${bullet.toLowerCase()}`
        });
      });
    }

    return probes;
  };

  // Generate discovery probes from strategic insights
  const getStrategicInsightsTopics = () => {
    if (!strategicInsightsData) return [];
    
    const topics = [];
    
    // Try to parse from structured analysis text first
    if (strategicInsightsData.analysisText || strategicInsightsData.rawAnalysis) {
      const analysisText = strategicInsightsData.analysisText || strategicInsightsData.rawAnalysis;
      const parsedProbes = parseAnalysisForDiscoveryProbes(analysisText);
      if (parsedProbes.length > 0) {
        console.log('🔍 Extracted discovery probes from structured analysis:', parsedProbes.length);
        topics.push(...parsedProbes);
      }
    }
    
    // Fallback to existing structure if available
    const { deeperInsights, competitiveAdvantages } = strategicInsightsData;
    
    if (topics.length === 0 && deeperInsights) {
      // Fallback parsing from existing data structure
      if (deeperInsights.untoldStories && deeperInsights.untoldStories.length > 0) {
        deeperInsights.untoldStories.slice(0, 2).forEach((story, index) => {
          topics.push({
            id: `fallback-story-${index}`,
            title: story,
            description: 'A story angle that might reveal experiences you haven\'t fully explored yet',
            probeType: 'exploration',
            badges: [{ label: 'Discovery', type: 'primary' }],
            competitiveAdvantage: 'Shows sides of yourself that might not have come through in your assessment',
            isPowerStory: true,
            recommended: index === 0,
            fullText: story,
            discoveryPrompt: `Tell me about ${story.toLowerCase()}`
          });
        });
      }

      if (deeperInsights.authenticityGaps && deeperInsights.authenticityGaps.length > 0) {
        deeperInsights.authenticityGaps.slice(0, 1).forEach((gap, index) => {
          topics.push({
            id: `fallback-authenticity-${index}`,
            title: gap,
            description: 'An area where you might be holding back or being too strategic in your responses',
            probeType: 'authenticity',
            badges: [{ label: 'Authentic', type: 'philosophical' }],
            competitiveAdvantage: 'Shows willingness to be vulnerable and genuinely reflective',
            recommended: false,
            fullText: gap,
            discoveryPrompt: `Help me understand ${gap.toLowerCase()}`
          });
        });
      }
    }

    console.log('🎯 Generated discovery probes:', topics.length, topics.map(t => t.title));
    
    // Apply deduplication to discovery probes
    return removeDuplicateTopics(topics);
  };

  // If no insights, show need assessment message
  if (!insights?.essayStrategy?.essayTopics && !strategicInsightsData) {
    return (
      <div className="text-center py-12">
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm max-w-2xl mx-auto">
          <Target className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Strategic Assessment Required</h3>
          <p className="text-stone-600 mb-6">
            Complete your strategic assessment first to unlock personalized essay topics and development tools.
          </p>
          <button
            onClick={() => window.location.href = '/college/assessment'}
            className="bg-violet-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-violet-700 transition-colors"
          >
            Take Strategic Assessment
          </button>
        </div>
      </div>
    );
  }

  // Stabilize assessmentData reference to prevent infinite loops
  const assessmentDataRef = React.useRef(assessmentData);
  React.useEffect(() => {
    assessmentDataRef.current = assessmentData;
  }, [assessmentData]);

  // Process and enrich topics on mount or when insights change
  useEffect(() => {
    const processTopics = () => {
      const allTopics = [];
      
      // Handle different modes explicitly
      if (mode === 'insights') {
        // Force insights mode - load from strategic insights data
        if (strategicInsightsData) {
          const strategicTopics = getStrategicInsightsTopics();
          if (strategicTopics.length > 0) {
            console.log('🔮 Adding DEEPER INSIGHTS topics (untold stories):', strategicTopics.length);
            allTopics.push(...strategicTopics);
          }
        }
        
        // If no strategic topics found but mode is insights, show message
        if (allTopics.length === 0) {
          console.log('📭 No strategic insights available for insights mode');
        }
        
        const enriched = enrichAllTopics(allTopics, assessmentDataRef.current);
        setEnrichedTopics(enriched);
        return;
      }
      
      // Handle assessment mode or legacy sessionStorage behavior
      if (mode === 'assessment') {
        // Force assessment mode - ignore strategic insights data
        console.log('📊 Processing assessment topics only');
      } else if (strategicInsightsData && !mode) {
        // Legacy sessionStorage behavior - show insights if present
        const strategicTopics = getStrategicInsightsTopics();
        if (strategicTopics.length > 0) {
          console.log('🔮 Adding DEEPER INSIGHTS topics (untold stories):', strategicTopics.length);
          allTopics.push(...strategicTopics);
          
          // If we have strategic insights, we're in "deeper stories" mode
          // Don't mix with regular assessment topics to avoid confusion
          const enriched = enrichAllTopics(allTopics, assessmentDataRef.current);
          setEnrichedTopics(enriched);
          return;
        }
      }
      
      // Add goldStory as a featured topic
      const goldStory = insights?.essayStrategy?.goldStory;
      if (goldStory && (goldStory.story || goldStory.title)) {
        allTopics.push({
          id: 'gold_story',
          title: goldStory.title || "Your Best Story",
          description: goldStory.story || '',
          whyItWorks: goldStory.strategicFraming || '',
          differentiator: goldStory.competitiveAngle || '',
          isPowerStory: true,
          storyContext: goldStory.storyContext || null,
          sourceResponse: goldStory.sourceResponse || null,
          fullText: (goldStory.story || '') + ' ' + (goldStory.title || '')
        });
      }
      
      // Add other essay topics
      const additionalTopics = insights.essayStrategy.essayTopics.map((topicItem, index) => {
        if (typeof topicItem === 'object' && topicItem !== null) {
          return {
            id: `topic_${index}`,
            title: topicItem.topic || topicItem.title || `Essay Topic ${index + 1}`,
            description: topicItem.whyItWorks || topicItem.description || '',
            differentiator: topicItem.differentiator || '',
            storyContext: topicItem.storyContext || null,
            sourceResponse: topicItem.sourceResponse || null,
            relatedQuestion: topicItem.relatedQuestion || null,
            fullText: topicItem.topic || topicItem.title || ''
          };
        } else if (typeof topicItem === 'string') {
          const match = topicItem.match(/^"([^"]+)"\s*:\s*(.+)$/);
          if (match) {
            return {
              id: `topic_${index}`,
              title: match[1],
              description: match[2],
              fullText: topicItem
            };
          }
          return {
            id: `topic_${index}`,
            title: `Essay Topic ${index + 1}`,
            description: topicItem,
            fullText: topicItem
          };
        } else {
          return {
            id: `topic_${index}`,
            title: `Essay Topic ${index + 1}`,
            description: 'Topic details unavailable',
            fullText: ''
          };
        }
      });
      
      allTopics.push(...additionalTopics);
      
      // Apply deduplication to all topics (assessment topics)
      console.log('📋 Processing assessment topics with deduplication...');
      const deduplicatedTopics = removeDuplicateTopics(allTopics);
      
      // Enrich topics with metrics
      const enriched = enrichAllTopics(deduplicatedTopics, assessmentDataRef.current);
      setEnrichedTopics(enriched);
    };
    
    processTopics();
  }, [insights, strategicInsightsData, mode]); // Removed assessmentData from dependencies

  // Generate discovery questions for untold story probing
  const generateDiscoveryQuestions = async (topic) => {
    try {
      setQuestionsLoading(true);
      setQuestionGenerationError(null);
      
      console.log('🕵️ Generating discovery questions for probe:', topic.title, topic.probeType);
      
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-dynamic-questions',
          selectedTopic: topic,
          assessmentData: assessmentData,
          questionType: 'story_discovery',
          questionCount: 4,
          context: 'hidden_story_discovery',
          discoveryPrompt: topic.discoveryPrompt || `Tell me about ${topic.title.toLowerCase()}`,
          probeType: topic.probeType || 'exploration'
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const questions = data.questions || [];
      
      if (questions.length === 0) {
        throw new Error('No discovery questions generated');
      }
      
      console.log('🔍 Generated discovery questions:', questions.length);
      return questions;
    } catch (error) {
      console.error('Failed to generate discovery questions:', error);
      setQuestionGenerationError(error.message);
      return getStaticDiscoveryQuestions(topic);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Fallback discovery questions for story probing
  const getStaticDiscoveryQuestions = (topic) => {
    const baseQuestions = {
      exploration: [
        {
          id: 'discovery_moment',
          question: `Tell me about a specific moment when you first realized ${topic.title.toLowerCase()}. What was happening?`,
          placeholder: "Think about a concrete moment, situation, or conversation that stands out..."
        },
        {
          id: 'unexpected_experience',
          question: `Describe a time when something unexpected happened related to ${topic.title.toLowerCase()}. What surprised you?`,
          placeholder: "Consider experiences you haven't shared before or didn't think were important..."
        },
        {
          id: 'cost_or_struggle',
          question: `What has ${topic.title.toLowerCase()} cost you? What have you had to sacrifice or struggle with?`,
          placeholder: "Think about the difficult or challenging aspects you might not usually talk about..."
        },
        {
          id: 'vulnerability_story',
          question: `Tell me about a time when ${topic.title.toLowerCase()} made you feel vulnerable or uncertain. What was that like?`,
          placeholder: "Share a moment when you felt exposed, unsure, or had to take a risk..."
        }
      ],
      vulnerability: [
        {
          id: 'fear_or_doubt',
          question: `When have you felt most afraid or uncertain about ${topic.title.toLowerCase()}? What was going through your mind?`,
          placeholder: "Describe the thoughts, feelings, and situation when you felt most vulnerable..."
        },
        {
          id: 'failure_moment',
          question: `Tell me about a time when you failed or made a mistake related to ${topic.title.toLowerCase()}. What happened?`,
          placeholder: "Share a specific failure or setback and how it affected you..."
        },
        {
          id: 'internal_conflict',
          question: `Describe a time when you felt conflicted or torn about ${topic.title.toLowerCase()}. What was the internal struggle?`,
          placeholder: "Think about moments when you were pulled in different directions..."
        },
        {
          id: 'growth_through_pain',
          question: `How has struggle or difficulty related to ${topic.title.toLowerCase()} changed you? What did you learn?`,
          placeholder: "Reflect on how challenges shaped your perspective or character..."
        }
      ],
      narrative: [
        {
          id: 'hidden_story',
          question: `What's a story about ${topic.title.toLowerCase()} that you've never really told anyone? Why haven't you shared it?`,
          placeholder: "Think about experiences you've kept private or didn't think others would understand..."
        },
        {
          id: 'behind_the_scenes',
          question: `What was really happening behind the scenes during ${topic.title.toLowerCase()}? What didn't others see?`,
          placeholder: "Describe the private moments, internal dialogue, or unseen preparations..."
        },
        {
          id: 'turning_point',
          question: `Describe the exact moment when ${topic.title.toLowerCase()} changed everything for you. What shifted?`,
          placeholder: "Think about a specific pivotal moment that altered your path or perspective..."
        },
        {
          id: 'ripple_effect',
          question: `How did ${topic.title.toLowerCase()} impact other areas of your life in ways you didn't expect?`,
          placeholder: "Consider the unexpected consequences or changes in other relationships or activities..."
        }
      ],
      authenticity: [
        {
          id: 'real_vs_image',
          question: `How is the real story of ${topic.title.toLowerCase()} different from how you usually present it?`,
          placeholder: "Think about the gap between your public narrative and private experience..."
        },
        {
          id: 'messy_truth',
          question: `What's the messy, complicated, or imperfect part of ${topic.title.toLowerCase()} that you usually don't mention?`,
          placeholder: "Share the parts that don't fit neatly into success stories or lessons learned..."
        },
        {
          id: 'emotional_truth',
          question: `What emotions related to ${topic.title.toLowerCase()} do you rarely acknowledge or share with others?`,
          placeholder: "Consider feelings like anger, resentment, fear, or embarrassment..."
        },
        {
          id: 'ongoing_impact',
          question: `How does ${topic.title.toLowerCase()} still affect you today in ways others might not realize?`,
          placeholder: "Think about current thoughts, behaviors, or decisions influenced by this experience..."
        }
      ]
    };

    const probeType = topic.probeType || 'exploration';
    const questionSet = baseQuestions[probeType] || baseQuestions.exploration;
    
    return questionSet.slice(0, 4); // Return 4 discovery questions
  };

  // Generate personalized questions using AI
  const generatePersonalizedQuestions = async (topic) => {
    try {
      setQuestionsLoading(true);
      setQuestionGenerationError(null);
      
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-dynamic-questions',
          selectedTopic: topic,
          assessmentData: assessmentData,
          questionType: 'essay_development',
          questionCount: 5,
          context: 'strategic_essay_development'
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const questions = data.questions || [];
      
      if (questions.length === 0) {
        throw new Error('No questions generated');
      }
      
      return questions;
    } catch (error) {
      console.error('Failed to generate personalized questions:', error);
      setQuestionGenerationError(error.message);
      return getStaticFallbackQuestions(topic);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Fallback to static questions if AI generation fails
  const getStaticFallbackQuestions = (topic) => {
    return [
      {
        id: 'core_story',
        question: `What's the specific story or experience you want to tell for "${topic?.title}"?`,
        placeholder: "Describe the concrete situation, event, or experience that best illustrates this topic..."
      },
      {
        id: 'unique_perspective',
        question: "What makes your perspective on this topic different from what other students might write?",
        placeholder: "Consider your unique background, approach, or insights that set your story apart..."
      },
      {
        id: 'deeper_meaning',
        question: "What deeper insight or lesson emerged from this experience?",
        placeholder: "Think about what you learned about yourself, others, or the world..."
      },
      {
        id: 'future_connection',
        question: "How does this experience connect to your future goals or what you want to study in college?",
        placeholder: "Link this story to your academic interests, career goals, or college plans..."
      },
      {
        id: 'vivid_details',
        question: "What are the most vivid, specific details that would bring this story to life?",
        placeholder: "Think about sensory details, dialogue, specific moments, or concrete examples..."
      }
    ];
  };

  const currentQuestion = dynamicQuestions[currentQuestionIndex];

  const startDevelopment = async (topic) => {
    setSelectedTopic(topic);
    
    // Check if this is a discovery probe (from strategic insights) or development topic (from assessment)
    const isDiscoveryProbe = topic.probeType || topic.id?.startsWith('strategic-') || topic.id?.startsWith('exploration-') || topic.id?.startsWith('vulnerability-');
    
    if (isDiscoveryProbe) {
      setPhase('discovery');
      // Generate discovery questions that probe for untold stories
      const questions = await generateDiscoveryQuestions(topic);
      setDynamicQuestions(questions);
    } else {
      setPhase('development');
      // Generate development questions for existing stories
      const questions = await generatePersonalizedQuestions(topic);
      setDynamicQuestions(questions);
    }
    
    setCurrentQuestionIndex(0);
    setCurrentAnswer('');
    setDevelopmentResponses({});
  };

  const submitAnswer = () => {
    const newResponses = {
      ...developmentResponses,
      [currentQuestion.id]: currentAnswer.trim()
    };
    setDevelopmentResponses(newResponses);

    if (currentQuestionIndex < dynamicQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishDevelopment(newResponses);
    }
  };

  const skipQuestion = () => {
    const newResponses = {
      ...developmentResponses,
      [currentQuestion.id]: ''
    };
    setDevelopmentResponses(newResponses);

    if (currentQuestionIndex < dynamicQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishDevelopment(newResponses);
    }
  };

  const finishDevelopment = async (finalResponses) => {
    setPhase('processing');
    setIsProcessing(true);

    try {
      // Generate outline using AI
      const outline = await generateEssayOutline(selectedTopic, finalResponses);
      setFinalOutline(outline);
      setPhase('results');
      
      if (onComplete) {
        onComplete({
          type: 'strategic_essay_development',
          selectedTopic,
          responses: finalResponses,
          outline
        });
      }
    } catch (error) {
      console.error('Error generating outline:', error);
      // Create a fallback outline
      setFinalOutline(createFallbackOutline(selectedTopic, finalResponses));
      setPhase('results');
    }

    setIsProcessing(false);
  };

  const generateEssayOutline = async (topic, responses) => {
    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'format-insights',
          rawText: `Essay Topic Development Session:

Selected Topic: "${topic.title}"
Topic Description: ${topic.description}

Student Development Responses:
${Object.entries(responses).map(([key, value]) => {
  const question = dynamicQuestions.find(q => q.id === key);
  return `${question?.question}\nResponse: ${value || 'Skipped'}`;
}).join('\n\n')}

Strategic Context:
Student's Strategic Advantage: ${insights.strategicAdvantage}
Essay Theme: ${insights.essayStrategy?.theme}

Please create a detailed essay outline and development notes for this topic.`,
          studentSpecificConsiderations: `Create a magnetic essay outline that connects abstract strategic advantages to concrete, vivid stories. Find the uncommon angle on this topic and identify narrative tension. Map story elements directly to admissions committee priorities and position this topic as uniquely theirs to tell.`,
          personalityProfile: assessmentData?.personality || {},
          studentName: currentUser?.name || currentUser?.displayName || 'this student',
          synthesisGuidance: `Generate a comprehensive essay outline that creates competitive advantage. Focus on specific structural suggestions, exact opening approaches, and strategic positioning advice. Include surprise insights about their story and future success visualization.`,
          formattingInstructions: `Return a detailed markdown outline that includes:
- Essay structure with magnetic opening hooks
- Key points that differentiate from typical applicants
- Specific writing advice with exact language suggestions
- Connection to strategic advantage and competitive positioning
- Development steps that unlock their unique narrative
- Anti-generic positioning against common application tropes`,
          interviewType: 'strategic_essay_development',
          studentResponses: responses
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate outline');
      }

      const data = await response.json();
      return data.results;
    } catch (error) {
      console.error('Error calling insights API:', error);
      throw error;
    }
  };

  const createFallbackOutline = (topic, responses) => {
    return `# ${topic.title} - Essay Outline

## Core Story
${responses.core_story || 'Develop your main story or experience here'}

## Why This Matters
${responses.why_matters || 'Explain the personal significance and growth'}

## Unique Perspective
${responses.unique_angle || 'What makes your approach different'}

## College Connection
${responses.college_connection || 'Link to your future goals and interests'}

## Key Details to Include
${responses.key_details || 'Vivid details that bring the story to life'}

## Essay Structure Suggestions
1. **Opening Hook**: Start with a specific moment or scene
2. **Context Setting**: Provide necessary background
3. **Main Story**: Tell your core experience with details
4. **Reflection**: What you learned and how you grew
5. **Future Connection**: How this shapes your college/career goals
6. **Closing**: Return to opening theme with new understanding

## Development Notes
- Focus on specific, concrete examples
- Show growth and learning
- Connect to your broader goals
- Use vivid, sensory details
- Keep the focus on your personal experience and insights
`;
  };

  // Simple save function - saves topic as-is without development
  const saveTopicToIdeas = async (topic) => {
    try {
      console.log('💾 Saving topic to Ideas...', topic.title);
      
      // Set loading state for this specific topic
      setSavingTopicId(topic.id);
      
      if (!currentUser?.id) {
        console.error('No current user ID');
        toast.error('Please log in to save ideas');
        setSavingTopicId(null);
        return;
      }

      // Format in the same structure as interview-generated ideas
      // Map Strategic Assessment fields to create a substantive card
      
      // Get the main content fields
      const mainDescription = topic.description || '';
      const whyThisWinsText = topic.whyThisWins || '';
      const differentiatorText = topic.differentiator || '';
      
      // For the main description box, use the primary description
      // The "Why This Works" and "Differentiator" will be displayed in separate sections
      const displayDescription = mainDescription || whyThisWinsText;
      
      // Get essay angle - keep it simple and direct
      const angleText = topic.essayAngle || (topic.badges && topic.badges[0]?.label) || '';
      
      // If there's a storyContext or relatedQuestion, use it for source_quote
      const sourceQuoteText = topic.storyContext || topic.relatedQuestion || '';
      
      const ideaData = {
        user_id: currentUser.id,
        source_type: 'strategic_assessment',
        title: topic.title,
        topic: topic.title, // For backward compatibility
        // Main description for the card display box
        description: displayDescription,
        // Content field for additional context
        content: differentiatorText,
        // Essay angle
        essay_angle: angleText,
        // Narrative arc - Strategic Assessment topics don't have this until developed
        narrative_arc: topic.narrativeArc || '',
        // Source quote from assessment context
        source_quote: sourceQuoteText,
        // Why it works - displayed in its own section
        why_it_works: whyThisWinsText,
        // Differentiator - displayed in its own section
        differentiator: differentiatorText,
        // Potential prompts - Strategic Assessment topics may not have these
        potential_prompts: topic.potentialPrompts || [],
        interview_data: {
          specificTopic: topic,
          savedFrom: 'strategic_topics_selection',
          // Preserve all the rich context for later development
          fullContext: {
            description: topic.description,
            whyItWorks: topic.whyItWorks,
            whyThisWins: topic.whyThisWins,
            differentiator: topic.differentiator,
            metrics: topic.metrics,
            badges: topic.badges,
            estimatedTime: topic.estimatedTime || topic.metrics?.estimatedTime,
            storyContext: topic.storyContext,
            relatedQuestion: topic.relatedQuestion,
            fullText: topic.fullText
          }
        },
        category: 'strategic-saved',
        tags: ['strategic_assessment', ...(topic.badges?.map(b => b.label || b) || []).slice(0, 3)]
      };

      const { data, error } = await supabase
        .from('college_journey_essay_ideas')
        .insert([ideaData])
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Topic saved successfully:', data);
      
      // Show success message
      toast.success(`"${topic.title}" saved to your Ideas library!`, {
        duration: 3000,
        icon: '✅'
      });
      
      // Notify parent to refresh library AND navigate to it
      if (onComplete) {
        onComplete({ 
          savedIdea: data,
          navigateToLibrary: true, // Signal parent to navigate to library
          selectedIdeaId: data.id // Pass the ID to highlight the new card
        });
      }
      
      setSavingTopicId(null);
      
    } catch (error) {
      console.error('❌ Error saving topic:', error);
      toast.error('Failed to save topic: ' + (error.message || 'Unknown error'));
      setSavingTopicId(null);
    }
  };

  // Save function after development - includes development outline and responses
  const saveToIdeas = async () => {
    try {
      console.log('💾 Saving developed topic to Ideas...');
      
      if (!currentUser?.id) {
        console.error('No current user ID');
        toast.error('Please log in to save ideas');
        return;
      }

      // Format in the same structure as interview-generated ideas
      const ideaData = {
        user_id: currentUser.id,
        source_type: 'strategic_assessment',
        title: selectedTopic.title,
        topic: selectedTopic.title, // For backward compatibility
        description: selectedTopic.story || selectedTopic.description || '',
        content: selectedTopic.whyItWorks || '',
        essay_angle: selectedTopic.essayAngle || '',
        narrative_arc: selectedTopic.narrativeArc || '',
        source_quote: selectedTopic.sourceQuote || '',
        why_it_works: selectedTopic.whyItWorks || '',
        differentiator: selectedTopic.differentiator || '',
        potential_prompts: selectedTopic.potentialPrompts || [],
        interview_data: {
          specificTopic: selectedTopic,
          developmentOutline: finalOutline,
          developmentResponses: developmentResponses,
          savedFrom: 'strategic_topics_development'
        },
        category: 'strategic-developed',
        tags: ['strategic_assessment', 'developed', ...(selectedTopic.potentialPrompts || [])]
      };

      const { data, error } = await supabase
        .from('college_journey_essay_ideas')
        .insert([ideaData])
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Developed topic saved successfully:', data);
      
      // Notify parent if onComplete callback exists
      if (onComplete) {
        onComplete({ savedIdea: data });
      }
      
      // Show success message
      toast.success('Developed essay topic saved to your Ideas library!');
      
    } catch (error) {
      console.error('❌ Error saving developed topic:', error);
      toast.error('Failed to save idea: ' + (error.message || 'Unknown error'));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      submitAnswer();
    }
  };

  const toggleCardExpansion = (topicId) => {
    setExpandedCards(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  return (
    <div className="space-y-6">
      {phase === 'selection' && (
        <div className="space-y-8">
          {/* Header */}
          <div className="bg-white border-b border-stone-200 -mx-6 -mt-6 px-6 py-6 mb-8">
            <div className="max-w-5xl mx-auto">
              <button
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-stone-600 text-sm mb-6 hover:text-slate-900 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Ideas Hub
              </button>

              <div className="mb-2">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  {mode === 'insights' ? 'Hidden Stories & Deeper Insights' : 'Your Strategic Essay Topics'}
                </h1>

                <p className="text-stone-600 text-sm">
                  {mode === 'insights'
                    ? 'Based on your strategic assessment analysis, these are untold stories and deeper aspects of yourself that could make compelling essays.'
                    : 'Your strategic assessment generated the following essay topics based on what you\'ve already shared. Choose the one that resonates most with you to develop into a full essay outline.'
                  }
                </p>
              </div>

              {/* Topic Type Filters - Inline with header */}
              {mode !== 'insights' && enrichedTopics.some(t => t.id === 'gold_story') && (
                <div className="mt-6">
                  <div className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">
                    Topic Types
                  </div>
                  <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedTopicType('all')}
                  className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                    selectedTopicType === 'all'
                      ? 'border-slate-900 bg-white text-slate-900 font-medium'
                      : 'border-stone-300 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  All Topics
                </button>
                <button
                  onClick={() => setSelectedTopicType('top')}
                  className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                    selectedTopicType === 'top'
                      ? 'border-slate-900 bg-white text-slate-900 font-medium'
                      : 'border-stone-300 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  Top Pick
                </button>
                <button
                  onClick={() => setSelectedTopicType('featured')}
                  className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                    selectedTopicType === 'featured'
                      ? 'border-slate-900 bg-white text-slate-900 font-medium'
                      : 'border-stone-300 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  Featured Topics
                </button>
                <button
                  onClick={() => setSelectedTopicType('assessment')}
                  className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                    selectedTopicType === 'assessment'
                      ? 'border-slate-900 bg-white text-slate-900 font-medium'
                      : 'border-stone-300 bg-white text-stone-600 hover:border-stone-400'
                  }`}
                >
                  From Assessment
                </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Strategic Insights Legend - only show for strategic content */}
          {mode === 'insights' && (
            <div className="bg-white rounded-xl border border-violet-200 p-4 mb-6 shadow-sm max-w-4xl mx-auto">
              <h3 className="text-sm font-semibold text-violet-600 mb-3 uppercase tracking-wide">Hidden Story Types</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  <div>
                    <div className="font-semibold text-violet-800">🔮 Untold Stories</div>
                    <div className="text-violet-600 text-xs">Hidden narratives</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <div>
                    <div className="font-semibold text-emerald-800">Competitive Edge</div>
                    <div className="text-emerald-600 text-xs">Unique differentiators</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-azure-50 rounded-lg border border-azure-200">
                  <User className="w-5 h-5 text-azure-600" />
                  <div>
                    <div className="font-semibold text-azure-800">Authenticity</div>
                    <div className="text-azure-600 text-xs">Deeper self-expression</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Comparison Table */}
          <QuickComparisonTable 
            topics={enrichedTopics.filter(topic => {
              if (selectedTopicType === 'all') return true;
              if (selectedTopicType === 'top') return topic.id === 'gold_story' || topic.recommended || topic.isPowerStory;
              if (selectedTopicType === 'featured') return topic.recommended || topic.isPowerStory || topic.id?.startsWith('strategic-');
              if (selectedTopicType === 'assessment') return topic.id?.startsWith('topic_');
              return true;
            })} 
            onTopicClick={toggleCardExpansion}
            expandedTopic={Object.keys(expandedCards).find(key => expandedCards[key])}
          />

          {/* Expanded Topic Details - Show all topics by default */}
          <div className="max-w-5xl mx-auto">
            {enrichedTopics
              .filter(topic => {
                if (selectedTopicType === 'all') return true;
                if (selectedTopicType === 'top') return topic.id === 'gold_story' || topic.recommended || topic.isPowerStory;
                if (selectedTopicType === 'featured') return topic.recommended || topic.isPowerStory || topic.id?.startsWith('strategic-');
                if (selectedTopicType === 'assessment') return topic.id?.startsWith('topic_');
                return true;
              })
              .map((topic, index) => (
                <ExpandedTopicDetails
                  key={topic.id}
                  topic={topic}
                  index={index}
                  onSave={saveTopicToIdeas}
                  isSaving={savingTopicId === topic.id}
                />
              ))}
          </div>
        </div>
      )}

      {(phase === 'development' || phase === 'discovery') && questionsLoading && (
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-violet-500 to-azure-500 rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-4">
            {phase === 'discovery' ? 'Generating Discovery Questions' : 'Generating Personal Questions'}
          </h3>
          <p className="text-slate-600 mb-6">
            {phase === 'discovery' 
              ? 'Creating discovery questions to uncover untold stories and experiences you might not have shared yet...'
              : 'Creating personalized development questions based on your assessment responses...'
            }
          </p>
          {questionGenerationError && (
            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              Falling back to standard questions due to: {questionGenerationError}
            </div>
          )}
        </div>
      )}

      {(phase === 'development' || phase === 'discovery') && !questionsLoading && currentQuestion && (
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg max-w-4xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">
                {phase === 'discovery' ? 'Discovering: ' : 'Developing: '}"{selectedTopic.title}"
              </h3>
              <div className="text-sm text-slate-600">
                Question {currentQuestionIndex + 1} of {dynamicQuestions.length}
              </div>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  phase === 'discovery' ? 'bg-gradient-to-r from-violet-600 to-pink-600' : 'bg-violet-600'
                }`}
                style={{ width: `${((currentQuestionIndex + 1) / dynamicQuestions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-slate-900 mb-4">
                {currentQuestion.question}
              </h4>
              <textarea
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={currentQuestion.placeholder}
                className="w-full p-4 border border-stone-300 rounded-lg resize-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                rows={6}
                autoFocus
              />
              <p className="text-sm text-slate-500 mt-2">
                Press Ctrl+Enter to submit, or use the buttons below
              </p>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setPhase('selection');
                  setSelectedTopic(null);
                  setCurrentQuestionIndex(0);
                  setCurrentAnswer('');
                  setDevelopmentResponses({});
                }}
                className="text-slate-600 hover:text-slate-900 font-medium"
              >
                ← Back to topic selection
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={skipQuestion}
                  className="bg-stone-100 text-stone-600 px-4 py-2 rounded-lg font-medium hover:bg-stone-200 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={submitAnswer}
                  className="bg-violet-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-violet-700 transition-colors flex items-center gap-2"
                >
                  {currentQuestionIndex < dynamicQuestions.length - 1 ? (
                    <>Next <ChevronRight className="w-4 h-4" /></>
                  ) : (
                    <>Generate Outline <Lightbulb className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {phase === 'processing' && (
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-violet-500 to-azure-500 rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-4">Creating Your Essay Outline</h3>
          <p className="text-slate-600 mb-6">
            We're analyzing your responses to create a detailed outline and development strategy for "{selectedTopic?.title}".
          </p>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
            <p className="text-sm text-violet-700">
              <strong>Please wait...</strong> Generating structure, key points, and writing guidance tailored to your story.
            </p>
          </div>
        </div>
      )}

      {phase === 'results' && finalOutline && (
        <div className="space-y-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-50 rounded-2xl p-8 border border-emerald-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Your Essay Outline is Ready!</h2>
                <p className="text-emerald-700">"{selectedTopic.title}" development complete</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Essay Development Notes</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={saveToIdeas}
                  className="bg-azure-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-azure-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Save to Ideas
                </button>
                <button
                  onClick={() => {
                    if (addEssay) {
                      const essayData = {
                        title: selectedTopic.title,
                        content: `Essay Topic: ${selectedTopic.title}\n\n${selectedTopic.description}\n\n---\n\n${typeof finalOutline === 'string' ? finalOutline : JSON.stringify(finalOutline, null, 2)}`,
                        type: 'personal',
                        source: 'strategic_topics_development'
                      };
                      addEssay(essayData);
                    }
                  }}
                  className="bg-violet-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-violet-700 transition-colors flex items-center gap-2"
                >
                  <Edit3 className="w-4 h-4" />
                  Start Essay Draft
                </button>
              </div>
            </div>
            
            <div className="bg-stone-50 rounded-lg p-6 overflow-auto">
              <pre className="whitespace-pre-wrap font-mono text-sm text-slate-800">
                {typeof finalOutline === 'string' ? finalOutline : JSON.stringify(finalOutline, null, 2)}
              </pre>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => {
                setPhase('selection');
                setSelectedTopic(null);
                setCurrentQuestionIndex(0);
                setCurrentAnswer('');
                setDevelopmentResponses({});
                setFinalOutline(null);
              }}
              className="text-violet-600 hover:text-violet-800 font-medium"
            >
              ← Develop another topic
            </button>
          </div>
        </div>
      )}
    </div>
  );
}